<template>
  <!-- Password Modal (auth gate) -->
  <div v-if="!isAuthenticated" class="password-modal-overlay">
    <div class="password-modal">
      <h2>AI Comment Reply</h2>
      <p>Enter password to access</p>
      <input
        type="password"
        v-model="password"
        @keyup.enter="verifyPassword"
        placeholder="Password"
        class="password-input"
        autofocus
      />
      <p v-if="passwordError" class="error-text">{{ passwordError }}</p>
      <button @click="verifyPassword" class="btn-primary">Enter</button>
    </div>
  </div>

  <dashboard-layout v-else title="AI Comment Reply">
    <template #actions>
      <status-badge v-if="isAdmin" variant="warning" label="Admin" />
      <button @click="startNewPost" class="btn-primary">+ New Post</button>
      <button @click="logout" class="btn-logout">Logout</button>
    </template>

    <stat-grid>
      <stat-card label="Posts" :value="roleThreads.length.toLocaleString('en-US')" icon="reports" accent />
      <stat-card label="Replies" :value="totalReplies.toLocaleString('en-US')" icon="messagecircle" />
      <stat-card label="TikTok" :value="tiktokCount.toLocaleString('en-US')" icon="activity" />
      <stat-card label="Instagram" :value="instagramCount.toLocaleString('en-US')" icon="share" />
    </stat-grid>

    <div class="content-area">
      <!-- Thread List Sidebar -->
      <data-panel flush class="thread-panel">
        <template #title>Posts <span class="muted">({{ filteredThreads.length }})</span></template>
        <template #actions>
          <search-box v-model="threadSearch" placeholder="Search posts…" />
        </template>
        <div class="thread-list">
          <div
            v-for="thread in filteredThreads"
            :key="thread.id"
            :class="['thread-item', { active: selectedThread?.id === thread.id }]"
            @click="selectThread(thread)"
          >
            <div class="thread-header-row">
              <span :class="['platform-badge', thread.platform]">
                {{ thread.platform === 'tiktok' ? 'TikTok' : 'Instagram' }}
              </span>
              <span class="reply-count">{{ (thread.replies || []).length }} replies</span>
            </div>
            <div class="thread-description">{{ truncate(thread.postDescription, 60) || 'No description' }}</div>
            <div class="thread-meta-row">
              <span class="thread-date">{{ formatDate(thread.updatedAt) }}</span>
              <span v-if="isAdmin" class="worker-tag">{{ thread.workerTag }}</span>
            </div>
          </div>
          <div v-if="filteredThreads.length === 0" class="no-threads">
            No posts found
          </div>
        </div>
      </data-panel>

      <!-- Reply Generator Area -->
      <div class="generator-area">
        <template v-if="selectedThread">
          <!-- Post Header -->
          <div class="post-header">
            <div class="post-header-info">
              <span :class="['platform-badge large', selectedThread.platform]">
                {{ selectedThread.platform === 'tiktok' ? 'TikTok' : 'Instagram' }}
              </span>
              <a v-if="selectedThread.postUrl" :href="selectedThread.postUrl" target="_blank" class="post-url-link">
                {{ truncate(selectedThread.postUrl, 50) }}
              </a>
              <span v-else class="no-url">No URL</span>
            </div>
          </div>

          <!-- System Prompt (Collapsible) -->
          <div class="section-card">
            <div class="section-header clickable" @click="showSystemPrompt = !showSystemPrompt">
              <div class="header-toggle">
                <span class="toggle-icon">{{ showSystemPrompt ? '▼' : '▶' }}</span>
                <label>System Prompt</label>
              </div>
              <span class="hint">AI Instructions</span>
            </div>
            <textarea
              v-if="showSystemPrompt"
              v-model="selectedThread.systemPrompt"
              @blur="saveSystemPrompt"
              class="section-textarea"
              placeholder="AI instructions..."
            ></textarea>
          </div>

          <!-- Post Description -->
          <div class="section-card">
            <div class="section-header">
              <label>Post Description</label>
              <span class="hint">What is this post about?</span>
            </div>
            <textarea
              v-model="selectedThread.postDescription"
              @blur="savePostDescription"
              placeholder="Describe what the post is about..."
              class="section-textarea"
            ></textarea>
          </div>

          <!-- Comment Input -->
          <div class="section-card">
            <div class="section-header">
              <label>Comment to Reply</label>
              <span class="hint">Paste the comment you want to reply to</span>
            </div>
            <textarea
              v-model="commentInput"
              placeholder="Paste the comment here..."
              class="section-textarea"
              :disabled="isGenerating"
            ></textarea>
            <button
              @click="generateReply"
              :disabled="!commentInput.trim() || isGenerating"
              class="btn-generate"
            >
              <span v-if="isGenerating">Generating...</span>
              <span v-else>Generate Reply</span>
            </button>
          </div>

          <!-- Generated Replies -->
          <div v-if="generatedAnswers.length" class="options-container">
            <div
              v-for="(answer, index) in generatedAnswers"
              :key="index"
              :class="['section-card', 'option-card', 'option-' + index]"
            >
              <div class="section-header">
                <label>{{ answer.option }}</label>
                <button @click="copyAnswer(index)" class="btn-copy">
                  {{ copiedOption === index ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <div class="generated-reply-text">{{ answer.reply }}</div>
            </div>
          </div>

          <!-- Reply History -->
          <div class="history-section" v-if="selectedThread.replies?.length">
            <h3>Reply History</h3>
            <div class="history-list">
              <div v-for="reply in selectedThread.replies" :key="reply.id" class="history-item">
                <div class="history-comment">
                  <span class="history-label">Comment:</span>
                  {{ reply.comment }}
                </div>
                <div class="history-options" v-if="reply.answers?.length">
                  <div
                    v-for="(answer, idx) in reply.answers"
                    :key="idx"
                    :class="['history-option', 'option-' + idx]"
                  >
                    <span class="history-label">{{ answer.option }}:</span>
                    <span class="option-text">{{ answer.reply }}</span>
                    <button @click="copyHistoryReply(answer.reply)" class="btn-copy-small">Copy</button>
                  </div>
                </div>
                <!-- Fallback for old data with generatedReply -->
                <div v-else-if="reply.generatedReply" class="history-reply">
                  <span class="history-label">Reply:</span>
                  {{ reply.generatedReply }}
                  <button @click="copyHistoryReply(reply.generatedReply)" class="btn-copy-small">Copy</button>
                </div>
                <div class="history-meta">
                  <span class="history-time">{{ formatTime(reply.createdAt) }}</span>
                  <button @click="deleteReply(reply.id)" class="btn-delete-small">Delete</button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- New Post Form -->
        <div v-else-if="isCreatingNew" class="generator-area">
          <!-- Post Header -->
          <div class="post-header">
            <div class="post-header-info">
              <div class="platform-selector-inline">
                <button
                  :class="['platform-badge large clickable', { active: newThreadData.platform === 'tiktok' }, newThreadData.platform === 'tiktok' ? 'tiktok' : '']"
                  @click="newThreadData.platform = 'tiktok'"
                >
                  TikTok
                </button>
                <button
                  :class="['platform-badge large clickable', { active: newThreadData.platform === 'instagram' }, newThreadData.platform === 'instagram' ? 'instagram' : '']"
                  @click="newThreadData.platform = 'instagram'"
                >
                  Instagram
                </button>
              </div>
              <input
                v-model="newThreadData.postUrl"
                placeholder="Post URL (optional)"
                class="url-input"
              />
            </div>
          </div>

          <!-- System Prompt (Collapsible) -->
          <div class="section-card">
            <div class="section-header clickable" @click="showSystemPrompt = !showSystemPrompt">
              <div class="header-toggle">
                <span class="toggle-icon">{{ showSystemPrompt ? '▼' : '▶' }}</span>
                <label>System Prompt</label>
              </div>
              <span class="hint">AI Instructions</span>
            </div>
            <textarea
              v-if="showSystemPrompt"
              v-model="newThreadData.systemPrompt"
              class="section-textarea"
              placeholder="AI instructions..."
            ></textarea>
          </div>

          <!-- Post Description -->
          <div class="section-card">
            <div class="section-header">
              <label>Post Description</label>
              <span class="hint">What is this post about?</span>
            </div>
            <textarea
              v-model="newThreadData.postDescription"
              placeholder="Describe what the post is about... (e.g., 'This video shows a morning skincare routine with our new vitamin C serum. The creator demonstrates application technique and talks about the glowing results.')"
              class="section-textarea"
            ></textarea>
          </div>

          <!-- Comment Input -->
          <div class="section-card">
            <div class="section-header">
              <label>Comment to Reply</label>
              <span class="hint">Paste the comment you want to reply to</span>
            </div>
            <textarea
              v-model="newThreadData.comment"
              placeholder="Paste the comment here..."
              class="section-textarea"
              :disabled="isGenerating"
            ></textarea>
            <button
              @click="createAndGenerate"
              :disabled="!newThreadData.postDescription.trim() || !newThreadData.comment.trim() || isGenerating"
              class="btn-generate"
            >
              <span v-if="isGenerating">Generating...</span>
              <span v-else>Create & Generate Reply</span>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-branch">
          <empty-state
            icon="messagecircle"
            title="Select a post or create a new one"
            hint="Pick a post from the list, or start a new one to generate replies."
          />
          <button @click="startNewPost" class="btn-primary">+ New Post</button>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseAICommentService, DEFAULT_SYSTEM_PROMPT } from '../services/firebaseAICommentService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import SearchBox from './ui/SearchBox.vue'
import StatusBadge from './ui/StatusBadge.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'AICommentDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, SearchBox, StatusBadge, EmptyState
  },
  setup() {
    const service = new FirebaseAICommentService()

    // Authentication
    const isAuthenticated = ref(false)
    const currentWorker = ref('')
    const isAdmin = ref(false)
    const password = ref('')
    const passwordError = ref('')

    // Threads
    const threads = ref([])
    const selectedThread = ref(null)
    const threadSearch = ref('')

    // New post mode
    const isCreatingNew = ref(false)

    // New thread data - TEST DEFAULTS
    const newThreadData = ref({
      postUrl: '',
      platform: 'tiktok',
      postDescription: `Taurus Woman: The Earthy Queen of Loyalty & Sensuality
This 8-minute deep dive explores the fascinating world of Taurus women and what makes them truly unique in the zodiac.
Core Traits:
Taurus women are known for their grounded, reliable nature. Ruled by Venus, they embody sensuality, beauty, and an appreciation for life's finer things. They're the ones who will create a cozy home, cook an amazing meal, and make you feel completely secure.
In Love & Relationships:
A Taurus woman loves deeply and loyally. Once she commits, she's in it for the long haul. However, winning her heart takes patience — she doesn't rush into anything. She needs to feel safe and secure before opening up emotionally.
Strengths:
Incredibly dependable, patient, and hardworking. She has a natural talent for building wealth and creating stability. Her determination is unmatched — when she sets a goal, nothing can stop her.
Shadow Side:
Can be stubborn to a fault, possessive in relationships, and resistant to change. Her love for comfort might sometimes turn into laziness or materialistic tendencies.
Compatibility:
Best matches often include Cancer, Virgo, Capricorn, and Pisces — signs that appreciate her loyalty and can match her need for emotional and financial security.`,
      systemPrompt: DEFAULT_SYSTEM_PROMPT,
      comment: `I like your explanation of my materialism. Sometimes I feel guilty about but I like what I like.`
    })

    // System prompt visibility
    const showSystemPrompt = ref(false)

    // Reply generation
    const commentInput = ref('')
    const generatedAnswers = ref([])
    const isGenerating = ref(false)
    const copied = ref(false)
    const copiedOption = ref(null)

    // Threads visible to the current role (admin sees all, workers see their own)
    const roleThreads = computed(() => {
      if (isAdmin.value) return threads.value
      return threads.value.filter(t => t.workerTag === currentWorker.value)
    })

    // Computed - filter threads based on user role + search
    const filteredThreads = computed(() => {
      let result = roleThreads.value

      if (threadSearch.value) {
        const search = threadSearch.value.toLowerCase()
        result = result.filter(t =>
          (t.postDescription || '').toLowerCase().includes(search) ||
          (t.postUrl || '').toLowerCase().includes(search)
        )
      }
      return result
    })

    // Summary counts derived from the loaded (role-filtered) threads
    const totalReplies = computed(() =>
      roleThreads.value.reduce((sum, t) => sum + (t.replies || []).length, 0)
    )
    const tiktokCount = computed(() => roleThreads.value.filter(t => t.platform === 'tiktok').length)
    const instagramCount = computed(() => roleThreads.value.filter(t => t.platform === 'instagram').length)

    // Methods
    const verifyPassword = () => {
      const result = service.verifyPassword(password.value)
      if (result.valid) {
        isAuthenticated.value = true
        currentWorker.value = result.worker
        isAdmin.value = result.isAdmin
        passwordError.value = ''
        sessionStorage.setItem('aiCommentAuth', 'true')
        sessionStorage.setItem('aiCommentWorker', result.worker)
        sessionStorage.setItem('aiCommentIsAdmin', result.isAdmin ? 'true' : 'false')
        loadThreads()
      } else {
        passwordError.value = 'Invalid password'
      }
    }

    const logout = () => {
      isAuthenticated.value = false
      currentWorker.value = ''
      isAdmin.value = false
      password.value = ''
      selectedThread.value = null
      threads.value = []
      sessionStorage.removeItem('aiCommentAuth')
      sessionStorage.removeItem('aiCommentWorker')
      sessionStorage.removeItem('aiCommentIsAdmin')
    }

    const loadThreads = async () => {
      try {
        threads.value = await service.getAllThreads()
      } catch (error) {
        console.error('Error loading threads:', error)
      }
    }

    const selectThread = async (thread) => {
      try {
        const fullThread = await service.getThread(thread.id)
        selectedThread.value = fullThread
        isCreatingNew.value = false
        showSystemPrompt.value = false
        commentInput.value = ''
        generatedAnswers.value = []
        copied.value = false
      } catch (error) {
        console.error('Error loading thread:', error)
      }
    }

    const startNewPost = () => {
      selectedThread.value = null
      isCreatingNew.value = true
      generatedAnswers.value = []
      showSystemPrompt.value = false
      // TEST DEFAULTS - remove after testing
      newThreadData.value = {
        postUrl: 'https://www.tiktok.com/@prosperastral/video/7519172217846926597?q=astrology%20for%20girl&t=1770028163126',
        platform: 'tiktok',
        postDescription: `What's your Lilith sign? Unlock your Dark Feminine with my Lilith guide 🖤 Link in bio #astrology #zodiac #zodiacsigns #lilith #darkfeminine`,
        systemPrompt: DEFAULT_SYSTEM_PROMPT,
        comment: `how to expose truth through contadiction?`
      }
    }

    const cancelNewPost = () => {
      isCreatingNew.value = false
    }

    const createAndGenerate = async () => {
      if (!newThreadData.value.postDescription.trim() || !newThreadData.value.comment.trim() || isGenerating.value) return

      isGenerating.value = true
      generatedAnswers.value = []

      try {
        // Create thread first
        const threadId = await service.createThread({
          postUrl: newThreadData.value.postUrl,
          platform: newThreadData.value.platform,
          postDescription: newThreadData.value.postDescription,
          systemPrompt: newThreadData.value.systemPrompt,
          workerTag: currentWorker.value
        })

        // Generate reply
        const result = await service.generateReply(threadId, newThreadData.value.comment.trim())
        generatedAnswers.value = result.answers || []

        // Reload and select
        await loadThreads()
        const newThread = threads.value.find(t => t.id === threadId)
        if (newThread) {
          selectedThread.value = await service.getThread(threadId)
        }

        isCreatingNew.value = false
        newThreadData.value = { postUrl: '', platform: 'tiktok', postDescription: '', systemPrompt: DEFAULT_SYSTEM_PROMPT, comment: '' }
      } catch (error) {
        console.error('Error creating and generating:', error)
        generatedAnswers.value = [{ option: 'Error', reply: error.message }]
      } finally {
        isGenerating.value = false
      }
    }

    const createThread = async () => {
      try {
        const threadId = await service.createThread({
          ...newThreadData.value,
          workerTag: currentWorker.value
        })
        await loadThreads()
        const newThread = threads.value.find(t => t.id === threadId)
        if (newThread) {
          await selectThread(newThread)
        }
        isCreatingNew.value = false
        newThreadData.value = { postUrl: '', platform: 'tiktok', postDescription: '' }
      } catch (error) {
        console.error('Error creating thread:', error)
      }
    }

    const savePostDescription = async () => {
      if (selectedThread.value) {
        try {
          await service.updateThread(selectedThread.value.id, {
            postDescription: selectedThread.value.postDescription
          })
        } catch (error) {
          console.error('Error saving description:', error)
        }
      }
    }

    const saveSystemPrompt = async () => {
      if (selectedThread.value) {
        try {
          await service.updateThread(selectedThread.value.id, {
            systemPrompt: selectedThread.value.systemPrompt
          })
        } catch (error) {
          console.error('Error saving system prompt:', error)
        }
      }
    }

    const generateReply = async () => {
      if (!commentInput.value.trim() || !selectedThread.value || isGenerating.value) return

      isGenerating.value = true
      generatedAnswers.value = []
      copied.value = false

      try {
        const result = await service.generateReply(
          selectedThread.value.id,
          commentInput.value.trim()
        )
        generatedAnswers.value = result.answers || []

        // Refresh thread to get updated replies
        const updatedThread = await service.getThread(selectedThread.value.id)
        selectedThread.value = updatedThread
        await loadThreads()

        // Clear comment input for next one
        commentInput.value = ''
      } catch (error) {
        console.error('Error generating reply:', error)
        generatedAnswers.value = [{ option: 'Error', reply: error.message }]
      } finally {
        isGenerating.value = false
      }
    }

    const copyAnswer = async (index) => {
      try {
        const text = generatedAnswers.value[index]?.reply || ''
        await navigator.clipboard.writeText(text)
        copiedOption.value = index
        setTimeout(() => { copiedOption.value = null }, 2000)
      } catch (error) {
        console.error('Error copying:', error)
      }
    }

    const copyHistoryReply = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
      } catch (error) {
        console.error('Error copying:', error)
      }
    }

    const deleteReply = async (replyId) => {
      if (!selectedThread.value) return
      try {
        await service.deleteReply(selectedThread.value.id, replyId)
        const updatedThread = await service.getThread(selectedThread.value.id)
        selectedThread.value = updatedThread
        await loadThreads()
      } catch (error) {
        console.error('Error deleting reply:', error)
      }
    }

    const formatDate = (val) => {
      if (!val) return '-'
      const date = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
      return date.toLocaleDateString('tr-TR')
    }

    const formatTime = (val) => {
      if (!val) return ''
      const date = new Date(val)
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const truncate = (text, max) => {
      if (!text) return ''
      return text.length > max ? text.slice(0, max) + '...' : text
    }

    onMounted(() => {
      const savedAuth = sessionStorage.getItem('aiCommentAuth')
      const savedWorker = sessionStorage.getItem('aiCommentWorker')
      const savedIsAdmin = sessionStorage.getItem('aiCommentIsAdmin')
      if (savedAuth === 'true' && savedWorker) {
        isAuthenticated.value = true
        currentWorker.value = savedWorker
        isAdmin.value = savedIsAdmin === 'true'
        loadThreads()
      }
    })

    return {
      // Auth
      isAuthenticated,
      currentWorker,
      isAdmin,
      password,
      passwordError,
      verifyPassword,
      logout,

      // Threads
      threads,
      selectedThread,
      threadSearch,
      roleThreads,
      filteredThreads,
      selectThread,
      createThread,

      // Summary counts
      totalReplies,
      tiktokCount,
      instagramCount,

      // New post
      isCreatingNew,
      newThreadData,
      showSystemPrompt,
      startNewPost,
      cancelNewPost,
      createAndGenerate,

      // Reply generation
      commentInput,
      generatedAnswers,
      isGenerating,
      copied,
      copiedOption,
      generateReply,
      copyAnswer,
      copyHistoryReply,
      deleteReply,
      savePostDescription,
      saveSystemPrompt,

      // Utils
      formatDate,
      formatTime,
      truncate
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }

/* Header actions */
.btn-logout {
  padding: 10px 18px;
  background: rgba(255, 71, 87, 0.1);
  color: #ff4757;
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-logout:hover {
  background: rgba(255, 71, 87, 0.2);
}

/* Password Modal */
.password-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.password-modal {
  background: #1a1b2e;
  padding: 40px;
  border-radius: 16px;
  text-align: center;
  width: 340px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.password-modal h2 {
  margin: 0 0 10px 0;
  font-size: 24px;
}

.password-modal p {
  color: #a9a9b3;
  margin: 0 0 24px 0;
}

.password-input {
  width: 100%;
  padding: 14px;
  background: #0f0f23;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.password-input:focus {
  outline: none;
  border-color: #6c63ff;
}

.error-text {
  color: #ff4757;
  margin: 0 0 16px 0;
  font-size: 14px;
}

/* Content Area (master-detail workspace) */
.content-area {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

/* Thread Sidebar (DataPanel) */
.thread-panel {
  width: 320px;
  flex-shrink: 0;
}

.thread-panel :deep(.panel-actions) { width: 100%; }
.thread-panel :deep(.search-box) { width: 100%; }

.thread-list {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  padding: 8px;
}

.thread-item {
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 6px;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.thread-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.thread-item.active {
  background: rgba(108, 99, 255, 0.15);
  border-color: rgba(108, 99, 255, 0.3);
}

.thread-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.platform-badge {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
}

.platform-badge.tiktok {
  background: rgba(0, 0, 0, 0.3);
  color: #69C9D0;
  border: 1px solid #69C9D0;
}

.platform-badge.instagram {
  background: rgba(225, 48, 108, 0.15);
  color: #E1306C;
  border: 1px solid #E1306C;
}

.platform-badge.large {
  font-size: 12px;
  padding: 5px 12px;
}

.reply-count {
  font-size: 11px;
  color: #a9a9b3;
}

.thread-description {
  font-size: 12px;
  color: #ccc;
  margin-bottom: 4px;
  line-height: 1.4;
}

.thread-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.thread-date {
  font-size: 11px;
  color: #666;
}

.worker-tag {
  font-size: 10px;
  padding: 2px 6px;
  background: rgba(255, 193, 7, 0.15);
  border-radius: 4px;
  color: #ffc107;
}

.no-threads {
  text-align: center;
  padding: 20px;
  color: #a9a9b3;
  font-size: 13px;
}

/* Generator Area */
.generator-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.post-header {
  padding: 14px 16px;
  background: #1a1b2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.post-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.platform-selector-inline {
  display: flex;
  gap: 8px;
}

.platform-badge.clickable {
  cursor: pointer;
  opacity: 0.5;
  transition: all 0.2s;
}

.platform-badge.clickable:hover {
  opacity: 0.8;
}

.platform-badge.clickable.active {
  opacity: 1;
}

.url-input {
  flex: 1;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
}

.url-input:focus {
  outline: none;
  border-color: rgba(108, 99, 255, 0.5);
}

.url-input::placeholder {
  color: #666;
}

.post-url-link {
  color: #6c63ff;
  text-decoration: none;
  font-size: 14px;
}

.post-url-link:hover {
  text-decoration: underline;
}

.no-url {
  color: #666;
  font-size: 14px;
}

/* Section Cards */
.section-card {
  background: #1a1b2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
}

.options-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-card {
  flex: 1;
}

.option-card.option-0 {
  border-color: rgba(99, 179, 237, 0.3);
  background: rgba(99, 179, 237, 0.05);
}

.option-card.option-1 {
  border-color: rgba(183, 148, 244, 0.3);
  background: rgba(183, 148, 244, 0.05);
}

.option-card.option-2 {
  border-color: rgba(246, 173, 85, 0.3);
  background: rgba(246, 173, 85, 0.05);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.section-header.clickable {
  cursor: pointer;
  margin-bottom: 0;
  padding: 4px 0;
  transition: opacity 0.2s;
}

.section-header.clickable:hover {
  opacity: 0.8;
}

.header-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-icon {
  font-size: 10px;
  color: #666;
}

.section-header label {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.section-header .hint {
  font-size: 11px;
  color: #666;
}

.section-textarea {
  width: 100%;
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  line-height: 1.5;
}

.section-textarea:focus {
  outline: none;
  border-color: rgba(108, 99, 255, 0.5);
}

.section-card .section-header.clickable + .section-textarea {
  margin-top: 12px;
}

.btn-generate {
  width: 100%;
  padding: 14px 24px;
  margin-top: 12px;
  background: linear-gradient(135deg, #6c63ff, #5a52d5);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-generate:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(108, 99, 255, 0.4);
}

.btn-generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.generated-reply-text {
  padding: 16px;
  background: rgba(46, 213, 115, 0.1);
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.6;
  color: #fff;
  white-space: pre-wrap;
}

.btn-copy {
  padding: 6px 12px;
  background: rgba(46, 213, 115, 0.2);
  border: 1px solid rgba(46, 213, 115, 0.3);
  border-radius: 6px;
  color: #2ed573;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy:hover {
  background: rgba(46, 213, 115, 0.3);
}

/* History Section */
.history-section {
  background: #1a1b2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
}

.history-section h3 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #a9a9b3;
  font-weight: 500;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-item {
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.history-comment,
.history-reply {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 8px;
}

.history-label {
  font-weight: 600;
  color: #a9a9b3;
  margin-right: 6px;
}

.history-comment {
  color: #888;
}

.history-reply {
  color: #ccc;
}

.history-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.history-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.5;
}

.history-option.option-0 {
  background: rgba(99, 179, 237, 0.1);
  border: 1px solid rgba(99, 179, 237, 0.2);
}

.history-option.option-1 {
  background: rgba(183, 148, 244, 0.1);
  border: 1px solid rgba(183, 148, 244, 0.2);
}

.history-option.option-2 {
  background: rgba(246, 173, 85, 0.1);
  border: 1px solid rgba(246, 173, 85, 0.2);
}

.history-option .history-label {
  font-weight: 700;
  min-width: 20px;
}

.history-option.option-0 .history-label {
  color: #63b3ed;
}

.history-option.option-1 .history-label {
  color: #b794f4;
}

.history-option.option-2 .history-label {
  color: #f6ad55;
}

.history-option .option-text {
  flex: 1;
  color: #ccc;
}

.history-option .btn-copy-small {
  flex-shrink: 0;
}

.history-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.history-time {
  font-size: 11px;
  color: #666;
  flex: 1;
}

.btn-copy-small,
.btn-delete-small {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy-small {
  background: rgba(108, 99, 255, 0.1);
  border: 1px solid rgba(108, 99, 255, 0.2);
  color: #6c63ff;
}

.btn-copy-small:hover {
  background: rgba(108, 99, 255, 0.2);
}

.btn-delete-small {
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid rgba(255, 71, 87, 0.2);
  color: #ff4757;
}

.btn-delete-small:hover {
  background: rgba(255, 71, 87, 0.2);
}

/* Empty branch (no post selected) */
.empty-branch {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 340px;
  padding: 32px 24px;
  background: var(--panel-bg);
  border: 1px solid var(--panel-border);
  border-radius: var(--radius-lg);
}

/* Buttons */
.btn-primary {
  padding: 10px 18px;
  background: #6c63ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #5a52d5;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-area {
    flex-direction: column;
  }

  .thread-panel {
    width: 100%;
  }

  .thread-list {
    max-height: 260px;
  }
}

@media (max-width: 480px) {
  .section-card {
    padding: 12px;
  }

  .options-container {
    gap: 8px;
  }

  .generated-reply-text {
    font-size: 13px;
  }

  .history-option {
    flex-direction: column;
    gap: 6px;
  }

  .history-option .btn-copy-small {
    align-self: flex-end;
  }
}
</style>
